import { useAuth } from "@clerk/clerk-react";
import { useCallback, useEffect, useMemo, useState } from "react";

// Hono Worker origin. In production the SPA is served from lab.foundling.ai
// so relative URLs work. In dev, Vite proxies /api → http://localhost:8787.
const API_BASE = "";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(
  path: string,
  token: string | null,
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      "Content-Type": "application/json",
    },
  });
  const text = await res.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = text;
  }
  if (!res.ok) {
    const message =
      (body && typeof body === "object" && "error" in body
        ? String((body as { error: unknown }).error)
        : null) ?? `HTTP ${res.status}`;
    throw new ApiError(message, res.status, body);
  }
  return body as T;
}

/**
 * Hook that returns a token-aware request function. The token is fetched
 * fresh per call via Clerk's `getToken()`, so it's always current.
 */
export function useApiClient() {
  const { getToken } = useAuth();
  return useCallback(
    async <T>(path: string, init?: RequestInit): Promise<T> => {
      const token = await getToken();
      return request<T>(path, token, init);
    },
    [getToken],
  );
}

interface UseApiState<T> {
  data: T | null;
  error: ApiError | null;
  loading: boolean;
  refetch: () => void;
}

/**
 * useApi — minimal data-fetching hook.
 *
 * Pass a stable path (memoize if computed) and a cache-buster key to force
 * refetches. No caching, no dedup — intentional, the dataset is small and
 * the first pass should be simple to reason about.
 */
export function useApi<T>(path: string | null): UseApiState<T> {
  const client = useApiClient();
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState<boolean>(path !== null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    if (path === null) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    client<T>(path)
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(
            err instanceof ApiError
              ? err
              : new ApiError(String(err), 0, null),
          );
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [client, path, tick]);

  return useMemo(
    () => ({ data, error, loading, refetch }),
    [data, error, loading, refetch],
  );
}
